import { motion } from "framer-motion";
import { Heart, Target, Eye, Quote } from "lucide-react";

const FOUNDER = "https://static.prod-images.emergentagent.com/jobs/82e52e3c-f998-4a3d-91a1-430c4242e074/images/c28bb688f024c8b6f89ef28e90f834d28174daf599a6d4ae3b77117424b1e8fb.png";
const RICE_FIELD = "https://images.unsplash.com/photo-1761549849686-d1fd83a094aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxnb2xkZW4lMjByaWNlJTIwZmllbGR8ZW58MHx8fHwxNzc2NDE1NjcwfDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${RICE_FIELD})` }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
            className="max-w-3xl"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Our Story
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-brand-green leading-[0.95] mb-8">
              A Legacy of{" "}
              <span className="italic text-brand-gold">Quality</span>
            </h1>
            <div className="gold-line w-24 mb-6" />
            <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-2xl p-6">
              <p className="font-serif text-xl sm:text-2xl font-medium text-brand-green leading-snug">
                India's First company to offer 100% rice quality guarantee — full refund if standards are not met!
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section data-testid="company-story" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3"
            >
              <p className="text-brand-text text-lg font-sans leading-[1.8] mb-6">
                <span className="font-serif text-5xl font-light text-brand-green float-left mr-3 mt-1 leading-[0.85]">
                  P
                </span>
                .S.IMPEX was founded with a singular vision: to bring the finest
                Indian rice to dinner tables across the world. Based in the heart
                of Punjab, India's rice bowl, we have direct access to the most
                fertile paddy fields that produce the world-renowned Basmati rice.
              </p>
              <p className="text-brand-muted text-base font-sans leading-relaxed mb-6">
                Our journey began with a deep understanding of rice cultivation and
                a commitment to maintaining the highest quality standards at every
                step of the process from sourcing and milling to packaging and
                shipping. Today, under the brand name Fab Crops, we export premium
                rice to multiple continents, serving importers, distributors, and
                wholesale buyers who demand nothing but the best.
              </p>
              <p className="text-brand-muted text-base font-sans leading-relaxed">
                Every grain of Fab Crops rice undergoes rigorous quality checks to
                ensure consistent grain length, aroma, and purity. Our
                state-of-the-art processing facility ensures that only the finest
                rice reaches our customers, maintaining the royal legacy of Indian
                Basmati across global markets.
              </p>
              <p className="text-brand-muted text-base font-sans leading-relaxed mt-6">
                Our paddy is procured from the lands of Himalayan snow-fed rivers
                of North India — Haryana & Punjab. These are the hearts of basmati
                rice growing areas. From the selection of seeds to monitoring
                harvest and crop, everything is done very professionally so that we
                can procure the right produce at the right price. We have
                specialized personnel for the same.
              </p>
              <p className="text-brand-muted text-base font-sans leading-relaxed mt-6">
                We monitor the crop right from sowing stage and then procure the
                same from various mandis after analyzing the quality of harvest at
                each and every mandi. We ensure the healthiness, aroma and purity
                of the grain before the procurement. The team is totally focused
                for providing real value for money to our customers globally.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="bg-brand-sand rounded-3xl p-8 lg:p-10">
                <h3 className="font-serif text-2xl font-medium text-brand-green mb-6">
                  Why Fab Crops?
                </h3>
                <div className="flex flex-col gap-5">
                  {[
                    "Direct sourcing from Punjab's finest paddy farms",
                    "State-of-the-art processing & sortex facilities",
                    "FSSAI, APEDA & ISO certified operations",
                    "Custom packaging for private label clients",
                    "Competitive pricing with consistent quality",
                    "Dedicated logistics & shipment tracking",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2 shrink-0" />
                      <span className="text-brand-text text-sm font-sans">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section data-testid="founder-section" className="py-24 lg:py-32 bg-brand-green">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
                Founder & Visionary
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-bone leading-tight mb-8">
                Manjot Singh
              </h2>
              <div className="relative mb-8">
                <Quote size={32} className="text-brand-gold/30 absolute -top-2 -left-2" />
                <p className="text-brand-bone/80 text-lg font-serif italic leading-relaxed pl-8">
                  &ldquo;Our mission is simple: deliver the purest, most aromatic
                  rice from India's heartland to the world. Every grain of Fab
                  Crops carries the pride of Punjab and the promise of premium
                  quality.&rdquo;
                </p>
              </div>
              <p className="text-brand-bone/60 text-base font-sans leading-relaxed">
                With years of experience in the rice industry, Manjot Singh
                founded P.S.IMPEX in 2021 and built it into a trusted name in
                international rice exports. His hands-on approach to quality
                control and commitment to ethical business practices have earned
                the company a loyal network of importers across Africa,
                Indonesia, the Middle East, Europe, and Canada.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-brand-bone/5 rounded-3xl p-10 lg:p-12 border border-brand-gold/10"
            >
              <div className="flex flex-col gap-8">
                <div>
                  <p className="text-brand-gold font-sans font-semibold text-sm uppercase tracking-wider mb-2">Established</p>
                  <p className="font-serif text-5xl font-medium text-brand-bone">2021</p>
                </div>
                <div className="gold-line" />
                <div>
                  <p className="text-brand-gold font-sans font-semibold text-sm uppercase tracking-wider mb-2">Headquarters</p>
                  <p className="font-serif text-2xl font-medium text-brand-bone">Punjab, India</p>
                </div>
                <div className="gold-line" />
                <div>
                  <p className="text-brand-gold font-sans font-semibold text-sm uppercase tracking-wider mb-2">Export Markets</p>
                  <p className="font-serif text-2xl font-medium text-brand-bone">15+ Countries</p>
                </div>
                <div className="gold-line" />
                <div>
                  <p className="text-brand-gold font-sans font-semibold text-sm uppercase tracking-wider mb-2">Brand</p>
                  <p className="font-sans text-2xl font-bold text-brand-bone uppercase tracking-wide">FAB CROPS</p>
                  <p className="text-brand-bone/50 text-sm font-sans mt-1">We Make It Healthier!</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section data-testid="mission-vision" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Our Compass
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-green">
              What Drives Us
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: "Our Mission",
                text: "To deliver the finest quality Indian rice to global markets through sustainable sourcing, rigorous quality control, and reliable export partnerships.",
              },
              {
                icon: Eye,
                title: "Our Vision",
                text: "To become the most trusted rice brand from India, recognized worldwide for premium quality, organic integrity, and excellence in every grain.",
              },
              {
                icon: Heart,
                title: "Our Values",
                text: "Quality without compromise. Transparency in every transaction. Sustainability in our practices. Trust as the foundation of every partnership.",
              },
            ].map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                className="bg-white p-10 rounded-3xl shadow-[0_8px_32px_rgba(26,54,38,0.04)] border border-brand-gold/10 text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 flex items-center justify-center mx-auto mb-6">
                  <Icon size={24} className="text-brand-gold" />
                </div>
                <h3 className="font-serif text-2xl font-medium text-brand-green mb-4">
                  {title}
                </h3>
                <p className="text-brand-muted text-sm font-sans leading-relaxed">
                  {text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
